<script setup>
// NOTICE: KAPTION-INTEGRATION
// two-step-verification.AUTHENTICATOR.1 — "Add an authenticator app" (canvas:
// Authenticator). Only offered when the server says the method is available
// (ActiveRecord encryption keys present).
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import QRCode from 'qrcode';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import KaptionTwoStepAPI from 'dashboard/api/kaptionTwoStep';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import KaptionTwoStepCodeInput from './KaptionTwoStepCodeInput.vue';

const emit = defineEmits(['added']);
const { t } = useI18n();

const dialogRef = ref(null);
const codeInputRef = ref(null);
const secret = ref('');
const qrUrl = ref('');
const code = ref('');
const copied = ref(false);
const busy = ref(false);
const error = ref('');

const groupedSecret = value => (value || '').replace(/(.{4})/g, '$1 ').trim();

const open = async () => {
  secret.value = '';
  qrUrl.value = '';
  code.value = '';
  copied.value = false;
  error.value = '';
  dialogRef.value?.open();
  try {
    const { data } = await KaptionTwoStepAPI.startAuthenticator();
    secret.value = data.secret;
    qrUrl.value = await QRCode.toDataURL(data.provisioning_uri, {
      width: 272,
      margin: 1,
    });
  } catch (e) {
    error.value =
      e?.response?.data?.error || t('KAPTION_TWO_STEP.ERRORS.GENERIC');
  }
};

const close = () => dialogRef.value?.close();

const copySecret = async () => {
  await copyTextToClipboard(secret.value);
  copied.value = true;
};

const verify = async () => {
  if (busy.value || code.value.length !== 6) return;
  busy.value = true;
  error.value = '';
  try {
    const { data } = await KaptionTwoStepAPI.verifyAuthenticator(code.value);
    close();
    emit('added', { method: 'authenticator', status: data });
  } catch (e) {
    error.value = t('KAPTION_TWO_STEP.LOGIN.ERRORS.INVALID_CODE');
    codeInputRef.value?.clear();
  } finally {
    busy.value = false;
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @confirm="verify"
  >
    <div
      class="flex flex-col gap-5"
      data-testid="kpt-two-step-authenticator-dialog"
    >
      <div class="flex flex-col gap-2">
        <h3 class="text-base font-semibold text-n-slate-12">
          {{ $t('KAPTION_TWO_STEP.AUTH_DIALOG.TITLE') }}
        </h3>
        <p class="text-sm leading-relaxed text-n-slate-11">
          {{ $t('KAPTION_TWO_STEP.AUTH_DIALOG.DESC') }}
        </p>
      </div>
      <div
        class="flex items-center gap-4 p-3 border rounded-lg border-n-weak bg-n-alpha-black2/50"
      >
        <div
          class="flex items-center justify-center flex-shrink-0 overflow-hidden bg-white border rounded-lg size-[136px] border-n-weak"
        >
          <img
            v-if="qrUrl"
            :src="qrUrl"
            :alt="$t('KAPTION_TWO_STEP.AUTH_DIALOG.QR_ALT')"
            class="size-full"
          />
        </div>
        <div class="flex flex-col min-w-0 gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('KAPTION_TWO_STEP.AUTH_DIALOG.CANT_SCAN') }}
          </span>
          <span class="text-[13px] text-n-slate-11">
            {{ $t('KAPTION_TWO_STEP.AUTH_DIALOG.TYPE_KEY') }}
          </span>
          <div class="flex flex-col items-start gap-2">
            <code
              class="px-2 py-1.5 font-mono text-[13px] break-all border rounded-lg bg-n-solid-1 border-n-weak text-n-slate-12"
              data-testid="kpt-two-step-secret"
            >
              {{ groupedSecret(secret) || '····' }}
            </code>
            <Button
              type="button"
              faded
              sm
              :label="
                copied
                  ? $t('KAPTION_TWO_STEP.AUTH_DIALOG.COPIED')
                  : $t('KAPTION_TWO_STEP.AUTH_DIALOG.COPY')
              "
              :disabled="!secret"
              @click="copySecret"
            />
          </div>
        </div>
      </div>
      <KaptionTwoStepCodeInput
        ref="codeInputRef"
        v-model="code"
        :label="$t('KAPTION_TWO_STEP.AUTH_DIALOG.CODE_LABEL')"
        :disabled="!secret || busy"
        @complete="verify"
      />
      <p v-if="error" class="text-sm text-n-ruby-11" role="alert">
        {{ error }}
      </p>
    </div>
    <template #footer>
      <div class="flex gap-3">
        <Button
          type="button"
          slate
          faded
          class="flex-1"
          :label="$t('KAPTION_TWO_STEP.ACTIONS.CANCEL')"
          @click="close"
        />
        <Button
          type="submit"
          class="flex-1"
          :label="$t('KAPTION_TWO_STEP.ACTIONS.VERIFY')"
          :is-loading="busy"
          :disabled="busy || code.length !== 6"
          data-testid="kpt-two-step-authenticator-verify"
        />
      </div>
    </template>
  </Dialog>
</template>
